import React, { useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

/* -------------------- Shared Components -------------------- */

// Nucleus
function Nucleus() {
  return (
    <mesh>
      <sphereGeometry args={[0.2, 32, 32]} />
      <meshStandardMaterial
        color="#ff0000"
        emissive="#ff0000"
        emissiveIntensity={0.9}
      />
    </mesh>
  );
}

// Classical Electron
function Electron({ radius, speed, rotation, is3D }) {
  const ref = useRef();
  const angleRef = useRef(Math.random() * Math.PI * 2);

  useFrame((state, delta) => {
    angleRef.current += speed * delta;
    const angle = angleRef.current;
    let pos = new THREE.Vector3(
      radius * Math.cos(angle),
      radius * Math.sin(angle),
      0
    );

    if (is3D) {
      const euler = new THREE.Euler(rotation.x, rotation.y, rotation.z);
      pos.applyEuler(euler);
    }

    ref.current.position.set(pos.x, pos.y, pos.z);
  });

  return (
    <mesh ref={ref}>
      <sphereGeometry args={[0.05, 16, 16]} />
      <meshStandardMaterial
        color="#FFFF00"
        emissive="#FFFF00"
        emissiveIntensity={0.99}
      />
    </mesh>
  );
}

// Orbital Ring
function OrbitalRing({ radius, rotation, is3D }) {
  return (
    <mesh rotation={is3D ? [rotation.x, rotation.y, rotation.z] : [0, 0, 0]}>
      <torusGeometry args={[radius, 0.01, 16, 100]} />
      <meshBasicMaterial color="#00ffcc" wireframe />
    </mesh>
  );
}

// Quantum Electron (Probability Cloud)
function QuantumElectron({ radius, is3D }) {
  const ref = useRef();
  useFrame(() => {
    // Random position in spherical shell
    let theta = Math.random() * Math.PI * 2;
    let phi = Math.random() * Math.PI;
    let r = radius * (0.8 + Math.random() * 0.4); // slight variation
    let x = r * Math.sin(phi) * Math.cos(theta);
    let y = r * Math.sin(phi) * Math.sin(theta);
    let z = r * Math.cos(phi);

    if (!is3D) z = 0; // flatten to 2D

    ref.current.position.set(x, y, z);
  });

  return (
    <mesh ref={ref}>
      <sphereGeometry args={[0.05, 16, 16]} />
      <meshStandardMaterial
        color="#00ff00"
        emissive="#00ff00"
        emissiveIntensity={0.8}
      />
    </mesh>
  );
}

/* -------------------- Main Component -------------------- */
export default function AtomExplorerDual() {
  const [numElectrons, setNumElectrons] = useState(3);
  const [speed, setSpeed] = useState(2);
  const [radiusBase, setRadiusBase] = useState(0.5);
  const [is3D, setIs3D] = useState(true);

  // Random rotations for classical orbits
  const [orbitRotations] = useState(
    Array.from({ length: 10 }, () => ({
      x: Math.random() * Math.PI,
      y: Math.random() * Math.PI,
      z: Math.random() * Math.PI,
    }))
  );

  const electronRadii = Array.from(
    { length: numElectrons },
    (_, i) => radiusBase + i * 0.3
  );

  return (
    <div className="experiment-root">
      <h3>Atom Explorer Dual: Classical vs Quantum</h3>

      <div className="controls-panel">
        <label>
          Number of Electrons:
          <input
            type="number"
            min="1"
            max="10"
            value={numElectrons}
            onChange={(e) => setNumElectrons(Number(e.target.value))}
          />
        </label>
        <label>
          Orbit Speed:
          <input
            type="number"
            min="0.1"
            max="5"
            step="0.1"
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
          />
        </label>
        <label>
          Base Orbit Radius:
          <input
            type="number"
            min="0.2"
            max="2"
            step="0.1"
            value={radiusBase}
            onChange={(e) => setRadiusBase(Number(e.target.value))}
          />
        </label>
        <label>
          Mode:
          <select
            value={is3D ? "3D" : "2D"}
            onChange={(e) => setIs3D(e.target.value === "3D")}
          >
            <option value="2D">2D Orbits</option>
            <option value="3D">Random 3D Orbits</option>
          </select>
        </label>
      </div>

      <div
  style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "40px", // space between the two sides
    marginTop: "20px",
  }}
>
  {/* ---------- Classical Atom ---------- */}
  <div style={{ textAlign: "center" }}>
    <h4 style={{ color: "#00ffff", marginBottom: "8px" }}>Classical Atom</h4>
    <Canvas style={{ height: 400, width: 400 }} camera={{ position: [0, 0, 2.5] }}>
      <ambientLight intensity={0.5} />
      <pointLight position={[5, 5, 4]} />
      <OrbitControls enableZoom={true} />

      <Nucleus />
      {electronRadii.map((r, i) => (
        <Electron
          key={i}
          radius={r}
          speed={speed}
          rotation={orbitRotations[i]}
          is3D={is3D}
        />
      ))}
      {electronRadii.map((r, i) => (
        <OrbitalRing key={i} radius={r} rotation={orbitRotations[i]} is3D={is3D} />
      ))}
    </Canvas>
  </div>

  {/* ---------- Quantum Atom ---------- */}
  <div style={{ textAlign: "center" }}>
    <h4 style={{ color: "#00ffff", marginBottom: "8px" }}>Quantum Atom</h4>
    <Canvas style={{ height: 400, width: 400 }} camera={{ position: [0, 0, 2.5] }}>
      <ambientLight intensity={0.5} />
      <pointLight position={[5, 5, 4]} />
      <OrbitControls enableZoom={true} />

      <Nucleus />
      {electronRadii.map((r, i) => (
        <QuantumElectron key={i} radius={r} is3D={is3D} />
      ))}
      {electronRadii.map((r, i) => (
        <OrbitalRing key={i} radius={r} rotation={orbitRotations[i]} is3D={is3D} />
      ))}
    </Canvas>
  </div>
</div>

     

      <div className="info-panel">
        <p>
          Left = Classical Atom (fixed orbits), Right = Quantum Atom
          (probability clouds). Red = Nucleus, Cyan = Orbital paths, Yellow =
          Classical electrons, Green = Quantum electrons.
        </p>
      </div>
    </div>
  );
}
